<script setup lang="ts">
import ProductList from '~/components/plp/ProductList.vue';
import ProductListSkeleton from '~/components/plp/Skeleton.vue';
import FilterBar from '~/components/filter-bar/index.vue';
import { useRoute } from 'vue-router';
import {
	fetchNodeProducts,
	fetchProductsCount,
	useProductsCount,
	useProductsList,
} from '~/data/products';
import Description from '~/components/plp/Description.vue';
import NodesBar from '~/components/plp/NodesBar.vue';
import { useNodesQuery } from '~/data/nodes';
import { Separator } from 'radix-vue';
import SeoProvider from '~/components/plp/SeoProvider.vue';
import { useFilterParams } from '~/utils/use-filter-param';
import { metaDesc, metaTitle } from '~/utils/seo';
import WebPageSeo from '~/components/seo/WebPageSeo.vue';

const { $cathodeClient } = useNuxtApp();
const route = useRoute();
const { storeType, store } = useGlobalStore();

const routeName = 'nodes-handle';
const handle = route.params.handle as string;

const node_id = getNodeIdFromHandle(handle);

if (!node_id) {
	throw createError({
		statusCode: 404,
		message: 'Node id not found in handle',
		data: {
			url: route.fullPath,
		},
		fatal: true,
	});
}

const filters = useFilterParams(routeName);

const { data: nodes, suspense } = useNodesQuery({
	entity: 'nodes',
	handle: node_id,
});

onServerPrefetch(async () => {
	await suspense();
});

const count = useProductsCount({
	routeName,
	queryKey: ['nodes', handle, filters],
	queryFn: () =>
		fetchProductsCount({
			node_id: node_id!,
			...filters.value,
		}),
});

const {
	data,
	isLoading,
	hasNextPage,
	fetchNextPage,
	isFetchingNextPage,
	error: productListError,
} = useProductsList({
	routeName: 'nodes-handle',
	queryKey: ['nodes', handle, filters],
	queryFn: ({ pageParam: cursor }) => {
		return fetchNodeProducts(node_id, cursor as string, filters.value);
	},
});

const seoTitle = computed(() => {
	return metaTitle(data, store.id, storeType!);
});

const seoDescription = computed(() => {
	return metaDesc(data, store.id, storeType!, store.name!);
});

provide('seoTitle', seoTitle);
provide('seoDescription', seoDescription);

onMounted(() => {
	$cathodeClient.queueEvent('page_load', {
		source: `${window.location.origin}${route.path}`,
		page: 'PLP',
		statusCode: 200,
		referrer: document.referrer,
		collection_handle: route.params.handle as string,
	});
});

watch(isLoading, (isLoading) => {
	if (!isLoading && productListError.value) {
		throw createError({
			statusCode: 404,
			message: 'Failed to load products',
			data: {
				url: route.fullPath,
			},
			fatal: true,
		});
	}
});
</script>
<template>
	<SeoProvider v-slot="{ seo }" :handle="handle" :route-name="routeName">
		<WebPageSeo
			:data="seo"
			:item-list-data="data?.pages"
			:handle="handle"
			:count="count"
		/>
		<div>
			<PlpHeader :data="seo" :items-count="count" />
			<div
				v-if="nodes && nodes.length > 0"
				class="flex flex-col gap-3xl border-b border-gray-200 py-3xl"
			>
				<NodesBar :show-new="false" :feed-nodes="nodes" />
			</div>
			<ClientOnly>
				<FilterBar :handle="handle" :is-product-list-loading="isLoading" />
				<!-- <ProductCarousel
					v-if="
						!isNewArrivalsLoading &&
						newArrivalsCarousel &&
						!isNewArrivalEnabled &&
						newArrivalsCarousel.count > 5
					"
					:data="newArrivalsCarousel"
					:header="
						getNewArrivalsHeader(seo?.title, newArrivalsCarousel?.count || 0)
					"
				/> -->
				<!-- <ProductList
					v-if="isNewArrivalEnabled"
					class="bg-gradient-to-b from-[#FFF3F3] to-white via-white from-0% via-20%"
					:header="
						getNewArrivalsHeader(seo?.title, newArrivalsCarousel?.count || 0)
					"
					:infinite-loader="true"
					:hide-no-more-products="true"
					:fetch-next-page="newArrivalsFetchNextPage"
					:is-loading="isNewArrivalProductsLoading"
					:data="newArrivalProducts"
					:has-next-page="newArrivalsHasNextPage"
					:is-fetching-next-page="newArrivalsIsFetchingNextPage"
				/> -->
				<ProductList
					:infinite-loader="true"
					:fetch-next-page="fetchNextPage"
					:is-loading="isLoading"
					:data="data"
					:has-next-page="hasNextPage"
					:is-fetching-next-page="isFetchingNextPage"
					:scroll-threshold="0.6"
				/>
				<template #fallback>
					<FilterBarSkeleton />
					<ProductListSkeleton />
				</template>
			</ClientOnly>

			<div
				v-if="
					!!seo?.description &&
					!!seo?.title &&
					storeType === STORE_TYPE.MARKETPLACE
				"
				class="w-full"
			>
				<Separator class="seo-description-separator" />
				<Description :description="seo.description" :heading="seo.title" />
			</div>
		</div>
	</SeoProvider>
</template>

<style scoped>
.seo-description-separator {
	@apply !bg-gray-100 h-xl mt-5xl;
}
</style>
